"use client";
/*
 * Documentation:
 * DC_Text_Citation_Trigger_Mobile — https://app.subframe.com/4f62853ee923/library?component=DC_Text_Citation_Trigger_Mobile_f8a5b0a9-a47e-4036-8971-1d5fd9236a61
 * Link Button — https://app.subframe.com/4f62853ee923/library?component=Link+Button_a4ee726a-774c-4091-8c49-55b659356024
 */

import React from "react";
import * as SubframeCore from "@subframe/core";
import * as SubframeUtils from "../utils";
import { LinkButton } from "./LinkButton";

interface SourceItemProps extends React.HTMLAttributes<HTMLDivElement> {
  reference?: React.ReactNode;
  linkSlot?: React.ReactNode;
  className?: string;
}

const SourceItem = React.forwardRef<HTMLDivElement, SourceItemProps>(
  function SourceItem(
    { reference, linkSlot, className, ...otherProps }: SourceItemProps,
    ref
  ) {
    return (
      <div
        className={SubframeUtils.twClassNames(
          "flex flex-col items-start gap-4",
          className
        )}
        ref={ref}
        {...otherProps}
      >
        {reference ? (
          <span className="text-caption font-caption text-default-font">
            {reference}
          </span>
        ) : null}
        {linkSlot ? (
          <div className="flex flex-col items-start gap-4">{linkSlot}</div>
        ) : null}
      </div>
    );
  }
);

interface SourceItemLinkProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
  className?: string;
}

const SourceItemLink = React.forwardRef<HTMLDivElement, SourceItemLinkProps>(
  function SourceItemLink(
    { children, className, ...otherProps }: SourceItemLinkProps,
    ref
  ) {
    return (
      <div
        className={SubframeUtils.twClassNames(
          "flex flex-col items-start gap-4",
          className
        )}
        ref={ref}
        {...otherProps}
      >
        <LinkButton variant="brand" size="small">
          {children}
        </LinkButton>
      </div>
    );
  }
);

interface Dc_Text_Citation_Trigger_MobileRootProps
  extends React.HTMLAttributes<HTMLSpanElement> {
  type?: "default";
  citationIndex?: React.ReactNode;
  reference?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}

const Dc_Text_Citation_Trigger_MobileRoot = React.forwardRef<
  HTMLSpanElement,
  Dc_Text_Citation_Trigger_MobileRootProps
>(function Dc_Text_Citation_Trigger_MobileRoot(
  {
    type = "default",
    citationIndex,
    reference,
    children,
    className,
    ...otherProps
  }: Dc_Text_Citation_Trigger_MobileRootProps,
  ref
) {
  return citationIndex ? (
    <SubframeCore.Popover.Root>
      <SubframeCore.Popover.Trigger asChild={true}>
        <span
          className={SubframeUtils.twClassNames(
            "group/f8a5b0a9 cursor-pointer text-body font-body text-compcolortextcitationtrigger p-[4px] m-[-4px] hover:text-compcolortextcitationtriggerhover",
            className
          )}
          ref={ref}
          {...otherProps}
        >
          {citationIndex}
        </span>
      </SubframeCore.Popover.Trigger>
      <SubframeCore.Popover.Portal>
        <SubframeCore.Popover.Content
          side="top"
          align="center"
          sideOffset={4}
          asChild={true}
        >
          {children ? (
            <div className="flex flex-col items-start gap-1 rounded-md border border-solid border-neutral-border bg-default-background px-3 py-3 shadow-lg">
              {children}
            </div>
          ) : null}
        </SubframeCore.Popover.Content>
      </SubframeCore.Popover.Portal>
    </SubframeCore.Popover.Root>
  ) : null;
});

export const Dc_Text_Citation_Trigger_Mobile = Object.assign(
  Dc_Text_Citation_Trigger_MobileRoot,
  {
    SourceItem,
    SourceItemLink,
  }
);
